<template>
  <div
    class="w-20 h-screen bg-white flex flex-col items-center py-4 space-y-8 sticky top-0 border-r"
  >
    <!-- Logo / Navigation oben -->
    <div class="flex flex-col items-center space-y-4 flex-1">
      <router-link
        to="/feed"
        class="w-16 h-16 bg-white flex items-center justify-center mb-16"
      >
        <img
          src="../assets/images/favicon.png"
          alt="Logo"
          class="w-15 h-15 rounded-full object-cover"
        />
      </router-link>

      <!-- Wettbewerber-Bubbles -->
      <div
        class="flex-1 w-full overflow-y-auto max-h-96 flex flex-col items-center space-y-4 scrollbar-hide"
      >
        <div
          v-for="item in mainStore.sidebarItems"
          :key="item.wettbewerberid"
          class="group"
        >
          <button
            @click="navigateToDetail(item.wettbewerberid)"
            @mouseover="showTooltip($event, item.name)"
            @mouseleave="hideTooltip"
            class="w-12 h-12 rounded-full flex items-center justify-center border transition-shadow duration-300"
            :class="[
              item.backgroundColor,
              item.textColor,
              isActive(item.wettbewerberid)
                ? 'shadow-2xl border-4 border-[#B203BC]'
                : '',
            ]"
          >
            {{ item.abbreviation }}
          </button>
        </div>
      </div>
    </div>

    <!-- Profil + Invite-Fly-out -->
    <div class="relative mb-4">
      <button @click="toggleInvite" class="flex flex-col items-center space-y-2">
        <img
          class="w-10 h-10 rounded-full object-cover"
          src="../assets/images/3.jpg"
          alt="Profile"
        />
      </button>

      <!-- Fly-out -->
<!-- Invite-Modal via BaseModal -->
<teleport to="body">
  <transition name="fade">
    <BaseModal v-if="inviteOpen" @close="toggleInvite">
      <div class="p-6 w-full max-w-md">
        <h2 class="text-lg font-semibold mb-4">Enter the invite Key <span class="text-[#B203BC]">from your Company.</span></h2>
        <input
          v-model="inviteKey"
          type="text"
          placeholder="Enter invite Key …"
          class="w-full border rounded px-3 py-2 mb-4 text-sm"
        />
        <button
          :disabled="!inviteKey"
          @click="joinTenant"
          class="w-full bg-[#B203BC] text-white py-2 rounded disabled:opacity-50"
        >
          Verbinden
        </button>
        <p class="mt-2 text-xs text-center text-red-500" v-if="inviteMsg">{{ inviteMsg }}</p>
      </div>
    </BaseModal>
  </transition>
</teleport>

    </div>

    <!-- Tooltip via teleport -->
    <teleport to="body">
      <div
        v-if="tooltipVisible"
        :style="tooltipStyle"
        class="fixed bg-gray-800 text-white text-sm rounded-md px-2 py-1 z-50 transition-opacity duration-300 pointer-events-none"
      >
        {{ tooltipText }}
        <svg
          class="absolute top-1/2 transform -translate-y-1/2 left-0 mr-[-5px]"
          width="5"
          height="10"
          viewBox="0 0 5 10"
          xmlns="http://www.w3.org/2000/svg"
          :style="{ left: '-5px' }"
        >
          <polygon points="5,0 0,5 5,10" fill="#2d3748" />
        </svg>
      </div>
    </teleport>
  </div>
</template>

<script>
import { onMounted, ref, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useMainStore } from '../stores/useMainStore';
import { supabase } from '../supabase';
import BaseModal from './BaseModal.vue';


export default {
  name: 'AppSidebar',
  components: { BaseModal },
  setup() {
    const mainStore = useMainStore();
    const router = useRouter();
    const route = useRoute();

    /* ---------- Daten laden ---------- */
    onMounted(() => {
      mainStore.fetchSidebarItems();
    });

    /* ---------- Invite-Fly-out ---------- */
    const inviteOpen = ref(false);
    const inviteKey = ref('');
    const inviteMsg = ref('');

    const toggleInvite = () => {
      inviteOpen.value = !inviteOpen.value;
    };

    // Fly-out schließen, wenn man außerhalb klickt / Esc drückt
    const onGlobalClick = (e) => {
      if (
        inviteOpen.value &&
        !e.target.closest('.relative.mb-4') /* Container oben */
      ) {
        inviteOpen.value = false;
      }
    };
    const onEsc = (e) => {
      if (e.key === 'Escape') inviteOpen.value = false;
    };
    onMounted(() => {
      window.addEventListener('click', onGlobalClick);
      window.addEventListener('keyup', onEsc);
    });
    /* cleanup */
    watch(
      () => inviteOpen.value,
      (v) => {
        if (!v) inviteMsg.value = '';
      },
    );

    const joinTenant = async () => {
      inviteMsg.value = 'Sende …';
      const { error } = await supabase.rpc('join_tenant', {
        p_invite: inviteKey.value,
      });
      if (error) {
        inviteMsg.value = error.message;
        return;
      }
      inviteMsg.value = 'Erfolgreich ✓';
      await mainStore.reloadData();
      inviteKey.value = '';
    };

    /* ---------- Tooltip ---------- */
    const tooltipVisible = ref(false);
    const tooltipText = ref('');
    const tooltipStyle = ref({});

    const showTooltip = (event, text) => {
      tooltipText.value = text;
      tooltipVisible.value = true;
      const rect = event.currentTarget.getBoundingClientRect();
      tooltipStyle.value = {
        top: `${rect.top + rect.height / 2}px`,
        left: `${rect.right + 8}px`,
        transform: 'translate(0,-50%)',
        position: 'fixed',
      };
    };
    const hideTooltip = () => {
      tooltipVisible.value = false;
    };

    /* ---------- Navigation ---------- */
    const navigateToDetail = async (id) => {
      await router.push(`/details/${id}`);
    };
    const isActive = (id) => route.params.id === String(id);

    return {
      mainStore,
      navigateToDetail,
      isActive,
      tooltipVisible,
      tooltipText,
      tooltipStyle,
      showTooltip,
      hideTooltip,
      /* invite */
      inviteOpen,
      inviteKey,
      inviteMsg,
      toggleInvite,
      joinTenant,
    };
  },
};
</script>

<style scoped>
/* Scrollbar ausblenden, aber Scroll-Funktion beibehalten */
.scrollbar-hide {
  scrollbar-width: none;
}
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}

/* kleiner Fade für Fly-out */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
